import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Cog } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MachineMaster = () => {
  const [machines, setMachines] = useState([]);
  const [newMachine, setNewMachine] = useState({
    name: '',
    code: '',
    rated_hours_per_shift: 8,
    uptime_target_percent: 85
  });

  useEffect(() => {
    fetchMachines();
  }, []);

  const fetchMachines = async () => {
    try {
      const response = await axios.get(`${API}/machines`);
      setMachines(response.data);
    } catch (error) {
      console.error('Error fetching machines:', error);
    }
  };

  const handleCreate = async () => {
    if (!newMachine.name || !newMachine.code) {
      toast.error('Name and code are required');
      return;
    }

    try {
      await axios.post(`${API}/machines`, newMachine);
      toast.success('Machine created successfully');
      setNewMachine({ name: '', code: '', rated_hours_per_shift: 8, uptime_target_percent: 85 });
      fetchMachines();
    } catch (error) {
      toast.error('Failed to create machine');
    }
  };

  return (
    <div className="space-y-6">
      <Card data-testid="machine-create-card">
        <CardHeader>
          <CardTitle>Add New Machine</CardTitle>
          <CardDescription>Register production equipment for capacity planning</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Machine Name</label>
              <Input
                data-testid="input-machine-name"
                placeholder="CNC Lathe 1"
                value={newMachine.name}
                onChange={(e) => setNewMachine({ ...newMachine, name: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Code</label>
              <Input
                data-testid="input-machine-code"
                placeholder="CNC-L1"
                value={newMachine.code}
                onChange={(e) => setNewMachine({ ...newMachine, code: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Rated Hours/Shift</label>
              <Input
                data-testid="input-rated-hours"
                type="number"
                value={newMachine.rated_hours_per_shift}
                onChange={(e) => setNewMachine({ ...newMachine, rated_hours_per_shift: parseFloat(e.target.value) })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Uptime Target %</label>
              <Input
                data-testid="input-uptime-target"
                type="number"
                value={newMachine.uptime_target_percent}
                onChange={(e) => setNewMachine({ ...newMachine, uptime_target_percent: parseFloat(e.target.value) })}
              />
            </div>
          </div>
          <Button data-testid="create-machine-btn" onClick={handleCreate} className="w-full">
            <Plus className="w-4 h-4 mr-2" />
            Create Machine
          </Button>
        </CardContent>
      </Card>

      <Card data-testid="machine-list-card">
        <CardHeader>
          <CardTitle>
            <div className="flex items-center gap-2">
              <Cog className="w-5 h-5" />
              All Machines ({machines.length})
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Rated Hours/Shift</TableHead>
                <TableHead>Uptime Target</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {machines.map((machine) => (
                <TableRow key={machine.id} data-testid={`machine-row-${machine.id}`}>
                  <TableCell className="font-medium">{machine.name}</TableCell>
                  <TableCell>{machine.code}</TableCell>
                  <TableCell>{machine.rated_hours_per_shift} hrs</TableCell>
                  <TableCell>{machine.uptime_target_percent}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default MachineMaster;
